/**
 * A passagem de plantão (`TP-069`): `GET /casos/{id}/passagem`, o registro (`POST …/passagem`), a validação
 * (`POST …/passagem/validacao`) e a guia corrigida (`POST …/passagem/divergencias/{id}/guia-corrigida`). Toda escrita
 * devolve a passagem do Caso atualizada. Quem registra, valida ou corrige NÃO vai no corpo: o back lê da sessão.
 */

import { queryOptions } from '@tanstack/react-query';
import type { Schemas } from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { AO_VIVO } from '../api/casos';
import {
  ehGrupoDoItem,
  ehMotivoSemConferencia,
  type Conferencia,
  type DivergenciaDaPassagem,
  type LinhaDeDivergencia,
  type PassagemDePlantao,
  type PassagemDoCaso,
} from './tipos';

type FioDaPassagemDoCaso = Schemas['PassagemDoCasoResponse'];
type FioDaPassagem = Schemas['PassagemDePlantaoResponse'];
type FioDaDivergencia = Schemas['DivergenciaDaPassagemResponse'];
type FioDaSugestao = Schemas['DivergenciaSugeridaResponse'];

export const chavesDaPassagem = {
  doCaso: (casoId: string) => ['casos', casoId, 'passagem'] as const,
};

// O CHECK do banco garante o vocabulário; `outro` só cobre um token novo que a tela ainda não conhece.
function grupoOuOutro(valor: string | null | undefined) {
  return ehGrupoDoItem(valor) ? valor : 'outro';
}

function mapearDivergencia(f: FioDaDivergencia): DivergenciaDaPassagem {
  return {
    id: f.id,
    grupo: grupoOuOutro(f.grupo),
    campo: f.campo ?? '',
    noPedido: f.noPedido ?? null,
    noIw: f.noIw ?? null,
    sugerida: f.sugerida,
    aberta: f.estado !== 'corrigida',
    corrigidaEm: f.corrigidaEm ? new Date(f.corrigidaEm) : null,
    corrigidaPorNome: f.corrigidaPorNome ?? null,
  };
}

function mapearPassagem(f: FioDaPassagem): PassagemDePlantao {
  return {
    id: f.id,
    deNome: f.deNome ?? null,
    paraNome: f.paraNome ?? null,
    registradaEm: new Date(f.registradaEm),
    conferencia: f.conferencia === 'com_divergencia' ? 'com_divergencia' : 'sem_divergencia',
    validadaEm: f.validadaEm ? new Date(f.validadaEm) : null,
    validadaPorNome: f.validadaPorNome ?? null,
    divergencias: (f.divergencias ?? []).map(mapearDivergencia),
  };
}

function mapearSugestao(f: FioDaSugestao): LinhaDeDivergencia {
  return { grupo: grupoOuOutro(f.grupo), campo: f.campo ?? '', noPedido: f.noPedido ?? '', noIw: f.noIw ?? '', sugerida: true };
}

export function mapearPassagemDoCaso(fio: FioDaPassagemDoCaso): PassagemDoCaso {
  return {
    podeRegistrar: fio.podeRegistrar,
    casoEhSeu: fio.casoEhSeu,
    souParte: fio.souParte,
    passagem: fio.passagem ? mapearPassagem(fio.passagem) : null,
    conferenciaAutomatica: fio.conferenciaAutomatica ?? null,
    semConferencia: ehMotivoSemConferencia(fio.semConferencia) ? fio.semConferencia : null,
    sugestoes: (fio.sugestoes ?? []).map(mapearSugestao),
    processoInicial: fio.processoInicial
      ? fio.processoInicial.map((i) => ({ chave: i.chave ?? '', rotulo: i.rotulo ?? '', presente: i.presente }))
      : null,
  };
}

function caminho(casoId: string): string {
  return `/casos/${encodeURIComponent(casoId)}/passagem`;
}

export function passagemDoCaso(casoId: string) {
  return queryOptions({
    queryKey: chavesDaPassagem.doCaso(casoId),
    queryFn: ({ signal }) => requisitar<FioDaPassagemDoCaso>(caminho(casoId), { sinal: signal }).then(mapearPassagemDoCaso),
    ...AO_VIVO,
    // A leitura roda a conferência pedido x IW (busca de paciente, admissões e orçamento, com token de pessoa): voltar
    // o foco à janela não pode refazer essa ida ao IW, nem trocar as sugestões que a pessoa viu ao abrir o bloco.
    refetchOnWindowFocus: false,
  });
}

/**
 * Registra que recebeu. Com divergência, ao menos uma linha (`400 HANDOFF_DIVERGENCE_REQUIRED`); Caso fora de
 * `autorizado`, `409 HANDOFF_CASE_NOT_AUTHORIZED`; outra pessoa registrou antes, `409 HANDOFF_ALREADY_REGISTERED`.
 * `automatica` é o que a leitura do bloco devolveu: o registro não vai ao IW de novo, só leva a versão e quantas
 * sugestões ela fez para o evento (`D4`).
 */
export function registrarPassagem(
  casoId: string,
  conferencia: Conferencia,
  divergencias: readonly LinhaDeDivergencia[],
  automatica: { readonly versao: string | null; readonly sugeridas: number },
): Promise<PassagemDoCaso> {
  return requisitar<FioDaPassagemDoCaso>(caminho(casoId), {
    method: 'POST',
    corpo: {
      conferencia,
      conferenciaAutomatica: automatica.versao,
      divergenciasSugeridas: automatica.versao === null ? null : automatica.sugeridas,
      divergencias:
        conferencia === 'com_divergencia'
          ? divergencias.map((d) => ({
              grupo: d.grupo,
              campo: d.campo.trim(),
              noPedido: d.noPedido.trim() === '' ? null : d.noPedido.trim(),
              noIw: d.noIw.trim() === '' ? null : d.noIw.trim(),
              sugerida: d.sugerida,
            }))
          : [],
    },
  }).then(mapearPassagemDoCaso);
}

/** Quem passou ou recebeu volta `409 HANDOFF_VALIDATOR_IS_PARTY`. Validar de novo devolve a validação existente. */
export function validarPassagem(casoId: string): Promise<PassagemDoCaso> {
  return requisitar<FioDaPassagemDoCaso>(`${caminho(casoId)}/validacao`, { method: 'POST' }).then(mapearPassagemDoCaso);
}

export function marcarGuiaCorrigida(casoId: string, divergenciaId: string): Promise<PassagemDoCaso> {
  return requisitar<FioDaPassagemDoCaso>(
    `${caminho(casoId)}/divergencias/${encodeURIComponent(divergenciaId)}/guia-corrigida`,
    { method: 'POST' },
  ).then(mapearPassagemDoCaso);
}
